import { addCalendarDays, daysBetween } from './compute-next-occurrence';
import type { DueState, OccurrenceDecision, OccurrenceStatus } from './types';

/**
 * Why a decision was refused. Stable codes — the UI maps them to i18n keys,
 * never the message text.
 */
export type DecisionRefusal =
  | 'occurrence.alreadyPosted'
  | 'occurrence.alreadyDecided'
  | 'occurrence.posting'
  | 'occurrence.notDecided'
  | 'occurrence.sameDate'
  | 'occurrence.dateOutOfBounds'
  | 'occurrence.invalidDate';

export interface DecidableOccurrence {
  status: OccurrenceStatus;
  dueDate: string;
}

/** How far a reschedule may move an occurrence, in days, each way. */
export const RESCHEDULE_MIN_DAYS = -365;
export const RESCHEDULE_MAX_DAYS = 730;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Which decisions a given occurrence still accepts.
 *
 * The whole state machine in one place:
 *   pending           → approve | reschedule | skip | reject
 *   skipped/rejected  → reset (undo the decision, back to pending)
 *   posting           → nothing (another device holds the lock)
 *   posted            → nothing (undoing means deleting the payment itself,
 *                       which belongs to the payments module — a posted
 *                       occurrence must never silently unbook money)
 */
export function refuseDecision(
  occurrence: DecidableOccurrence,
  decision: OccurrenceDecision,
): DecisionRefusal | null {
  if (decision === 'reset') {
    if (occurrence.status === 'posted') return 'occurrence.alreadyPosted';
    if (occurrence.status === 'posting') return 'occurrence.posting';
    if (occurrence.status === 'pending') return 'occurrence.notDecided';
    return null;
  }

  switch (occurrence.status) {
    case 'pending':
      return null;
    case 'posting':
      return 'occurrence.posting';
    case 'posted':
      return 'occurrence.alreadyPosted';
    default:
      return 'occurrence.alreadyDecided';
  }
}

export function canDecide(
  occurrence: DecidableOccurrence,
  decision: OccurrenceDecision,
): boolean {
  return refuseDecision(occurrence, decision) === null;
}

/**
 * Validate a reschedule target.
 *
 * Back-dating is allowed on purpose (the rent really was paid last Thursday),
 * but a date a decade out is a typo, not an intent — so the window is bounded
 * on both sides and a no-op move is refused rather than written.
 */
export function refuseReschedule(
  occurrence: DecidableOccurrence,
  newDueDate: string,
  today: string,
): DecisionRefusal | null {
  const blocked = refuseDecision(occurrence, 'reschedule');
  if (blocked) return blocked;

  if (!ISO_DATE.test(newDueDate)) return 'occurrence.invalidDate';
  if (newDueDate === occurrence.dueDate) return 'occurrence.sameDate';

  const delta = daysBetween(today, newDueDate);
  if (delta < RESCHEDULE_MIN_DAYS || delta > RESCHEDULE_MAX_DAYS) {
    return 'occurrence.dateOutOfBounds';
  }

  return null;
}

/** Quick-pick targets offered by the reschedule dialog. */
export interface RescheduleSuggestion {
  key: 'plusWeek' | 'plusTwoWeeks' | 'plusMonth' | 'endOfMonth';
  date: string;
}

export function rescheduleSuggestions(dueDate: string): RescheduleSuggestion[] {
  const [y, m, d] = dueDate.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y!, m!, 0)).getUTCDate();
  const endOfMonth = `${dueDate.slice(0, 8)}${String(lastDay).padStart(2, '0')}`;

  // A real calendar month, not 30 days: "+ 1 mois" on the 1st must land on
  // the 1st, not on the 31st — which also stopped it colliding with
  // "Fin du mois" and looking like a bug.
  const nextMonthIndex = m!; // 0-based index of the FOLLOWING month
  const nextYear = y! + Math.floor(nextMonthIndex / 12);
  const nextMonth = (nextMonthIndex % 12) + 1;
  const nextMonthLastDay = new Date(Date.UTC(nextYear, nextMonth, 0)).getUTCDate();
  const plusMonth = `${nextYear}-${String(nextMonth).padStart(2, '0')}-${String(Math.min(d!, nextMonthLastDay)).padStart(2, '0')}`;

  const suggestions: RescheduleSuggestion[] = [
    { key: 'plusWeek', date: addCalendarDays(dueDate, 7) },
    { key: 'plusTwoWeeks', date: addCalendarDays(dueDate, 14) },
    { key: 'plusMonth', date: plusMonth },
  ];

  // Only worth offering when it actually moves the date forward.
  if (endOfMonth > dueDate) suggestions.push({ key: 'endOfMonth', date: endOfMonth });

  // Two chips proposing the same day is a bug to the eye — keep the first.
  const seen = new Set<string>();
  return suggestions.filter((s) => (seen.has(s.date) ? false : (seen.add(s.date), true)));
}

/** Whole days from today to the due date (negative = late). */
export function daysUntilDue(dueDate: string, today: string): number {
  return daysBetween(today, dueDate);
}

/**
 * Urgency bucket driving the inbox grouping and colour.
 * `soon` covers the next `soonDays` days; anything beyond is `upcoming`.
 */
export function classifyDueState(dueDate: string, today: string, soonDays = 3): DueState {
  const delta = daysUntilDue(dueDate, today);
  if (delta < 0) return 'overdue';
  if (delta === 0) return 'today';
  return delta <= soonDays ? 'soon' : 'upcoming';
}
